import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Globe, User } from "lucide-react";
import { LOGO_URL } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export default function Navbar() {
  const { t, toggle } = useLang();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const goAccount = () => {
    if (user) navigate(user.is_admin ? "/admin" : "/account");
    else navigate("/account");
  };

  const links = [
    { to: "/", label: t("nav_home") },
    { to: "/packages", label: t("nav_packages") },
    { to: "/gallery", label: t("nav_gallery") },
    { to: "/inquiry", label: t("nav_inquiry") },
    { to: "/track", label: t("nav_track") },
    { to: "/contact", label: t("nav_contact") },
  ];

  return (
    <header className="fixed top-0 inset-x-0 z-50 glass border-b border-pink-200">
      <nav className="max-w-7xl mx-auto px-5 lg:px-8 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center shrink-0">
          <img src={LOGO_URL} alt="Metrix Event Management" className="h-11 md:h-14 w-auto" />
        </Link>

        <div className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors hover:text-gold ${
                  isActive ? "text-gold" : "text-zinc-700"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={toggle}
            data-testid="lang-toggle-btn"
            className="hidden sm:flex items-center gap-1.5 text-xs tracking-wide text-zinc-700 hover:text-gold transition-colors px-3 py-2 rounded-full border border-pink-200"
          >
            <Globe size={14} /> {t("lang_toggle")}
          </button>
          <Button
            data-testid="nav-book-btn"
            onClick={() => navigate("/packages")}
            className="hidden sm:inline-flex gold-gradient text-white font-semibold rounded-full hover:-translate-y-0.5 transition-transform"
          >
            {t("book_now")}
          </Button>
          <button
            onClick={goAccount}
            data-testid="nav-account-btn"
            className="hidden sm:flex items-center gap-1.5 text-xs tracking-wide text-zinc-700 hover:text-gold transition-colors px-3 py-2 rounded-full border border-pink-200"
          >
            <User size={14} /> {user ? (user.is_admin ? "Admin" : "My Bookings") : "Login"}
          </button>
          <button
            className="lg:hidden text-zinc-900 p-2"
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-btn"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="lg:hidden glass border-t border-pink-200 px-5 py-4 flex flex-col gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              data-testid={`mobile-link-${l.to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `py-3 text-base border-b border-pink-100 ${isActive ? "text-gold" : "text-zinc-700"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <div className="flex gap-2 pt-3">
            <Button variant="outline" onClick={toggle} className="flex-1 border-pink-300 text-zinc-700" data-testid="mobile-lang-btn">
              <Globe size={14} className="mr-2" /> {t("lang_toggle")}
            </Button>
            <Button
              onClick={() => { setOpen(false); navigate("/packages"); }}
              className="flex-1 gold-gradient text-white font-semibold"
            >
              {t("book_now")}
            </Button>
          </div>
          <Button variant="outline" onClick={() => { setOpen(false); goAccount(); }} data-testid="mobile-account-btn" className="w-full mt-2 border-pink-200 text-zinc-700">
            {user ? (user.is_admin ? "Admin Panel" : "My Bookings") : "Login"}
          </Button>
        </div>
      )}
    </header>
  );
}
